<template>
  <PmwModal
    :value="value"
    width="auto"
    class="edition-dialog"
    smallHeader
    @input="$emit('input', !value)"
  >
    <template #header-content>
      <PmwHeading size="heading-20" class="edition-dialog--custom-title">
        {{ dayData.title }}
      </PmwHeading>
    </template>

    <!-- CONTEÚDO -->
    <template #default>
      <div class="edition-dialog--content">
        <div class="edition-dialog--content__scroll">
          <PmwHeading class="edition-dialog--title">
            {{ hotel }}
          </PmwHeading>

          <!-- TABELA -->
          <component
            :is="currentTableValue"
            :rooms="hotelData.quartos"
            :date="dayData.date"
            :loading="loading"
            class="edition-table"
          />
        </div>
      </div>
    </template>

    <!-- AÇÕES -->
    <template #footer-content>
      <div class="edition-dialog--actions">
        <PmwButton
          class="edition-dialog--action-btn"
          variation="blue-100"
          layout="outline"
          size="large"
          @click="$emit('input', false)"
          >Fechar</PmwButton
        >
      </div>
    </template>
  </PmwModal>
</template>

<script>
/**
 * Componente de edição das configurações dos quartos do hotel
 * para um dia específico do calendário
 */
import { mapGetters } from 'vuex'
import { parseISO } from 'date-fns'
import { formatDate } from '@/helpers/date'
import PmwModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwModal'
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import PmwButton from '@solucoes/components-lib-tmp/src/components/elements/PmwButton'
import DayInfoTable from '@/components/hotel/points/DayInfoTable'

export default {
  name: 'PointsDayEditionPmwModal',
  components: {
    PmwModal,
    PmwHeading,
    PmwButton,
    DayInfoTable
  },
  props: {
    /**
     * Dados da configuração do dia
     *
     * {
     *    title: String
     * }
     */
    dayData: {
      type: Object,
      required: true
    },
    /** Se deve mostrar a modal */
    value: {
      type: Boolean,
      default: false
    },
    hotel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      closeAll: true /** Fechar para todos */,
      ctaAll: true /** Checkin fechado para todos */,
      ctdAll: true /** Checkout fechado para todos */,
      minNightsDaysToAll: 0 /** Mínimo de noites para todos */,
      minNightsTypeToAll: {
        text: 'Chegando em',
        value: 1
      } /** Tipo de mínimo de noites para todos */,
      /** Dados do hotel */
      hotelData: {
        data: null,
        codigoHotel: null,
        quartos: []
      },
      disableSave: true /** Desabilita o botão de salvar */,
      savingDaily: false /** Se uma requisição para salvar já está sendo feita */,
      loading: false /** Controla o loading da tabela */
    }
  },
  computed: {
    allClosedChecked() {
      return this.hotelData.quartos.every((item) => item.fechado === true)
    },
    allCtaChecked() {
      return this.hotelData.quartos.every((item) => item.cta === true)
    },
    allCtdChecked() {
      return this.hotelData.quartos.every((item) => item.ctd === true)
    },
    /** Retorna o tipo da tabela que deve ser apresentada */
    currentTableValue() {
      return 'DayInfoTable'
    },
    ...mapGetters('hotel', ['isAbsoluteRate', 'getHotelCode'])
  },
  watch: {
    /** Observa o controlador de visualização */
    value(newVal) {
      /** Se atualizou o valor faz a requisição */
      if (newVal) {
        this.loading = true
        this.$calendar
          .getDailyPoints(
            this.getHotelCode,
            formatDate(new Date(this.dayData.date), 'yyyy-MM-dd')
          )
          .then(async (response) => {
            this.hotelData.codigoHotel = this.getHotelCode
            this.hotelData.data = formatDate(
              parseISO(response.data[0].data),
              'yyyy-MM-dd'
            )
            this.hotelData.quartos = response.data

            /**
             * Verifica as opções de retrições de fechamento
             * para incializar as seleções de marcar todos os quartos
             */
            this.hotelData.quartos.forEach((item) => {
              item.desconto = item.desconto === 0 ? null : item.desconto
              if (!item.fechado) {
                this.closeAll = false
              }
              if (!item.cta) {
                this.ctaAll = false
              }
              if (!item.ctd) {
                this.ctdAll = false
              }
            })

            /** Espera para atualizar no próximo ciclo de renderização */
            await this.$nextTick()

            /** Desabilita o botão de salvar ao carregar uma nova data */
            this.disableSave = true

            this.loading = false
          })
      }
    },
    /**
     * Observa mudanças em profundidade para
     * habilitar o botão de salvar
     */
    hotelData: {
      handler() {
        this.disableSave = false
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.edition-dialog {
  &--title {
    font-size: 26px;
    line-height: 35px;
    margin-bottom: 0;
  }

  &--custom-title {
    margin: 0;
    font-size: font-size(heading-20);
  }

  &--content {
    padding: {
      left: 30px;
      right: 30px;
      top: 35px;
      bottom: 10px;
    }
    overflow-x: hidden;

    &__scroll {
      overflow-x: auto;
      @include scrollbars(5px, $gray-mid, $white);
    }

    .check-all {
      display: flex;
      &-row__stick {
        position: sticky;
        left: -10px;
      }
    }

    .edition-table {
      padding-top: 33px;
      overflow-x: visible;
    }

    ::v-deep .input-checkbox {
      margin-right: 24px;

      &--label {
        width: auto;
      }
    }

    .restriction-all {
      margin-top: 35px;
      position: sticky;
      left: -10px;
      padding-bottom: 30px;

      input {
        border: none;
        margin: 0 15px;
        padding-left: 10px;
        font-size: font-size('size-medium');
        @include placeholder {
          color: $gray-mid;
        }
      }

      span {
        font-size: font-size('size-medium');
      }

      .apply-to-all {
        margin-left: 20px;
      }

      .select__borderless {
        width: 140px;
        display: inline-block;

        ::v-deep .select--button {
          border: none;
        }
      }
    }
  }

  &--actions {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }

  &--action-btn {
    margin-left: 10px;
  }
}

.general-min-nights {
  @include reset-input();
}

::v-deep .modal-component--close-button {
  padding: {
    top: 13px;
    bottom: 13px;
  }
  height: 12px;

  svg.icon {
    width: 12px;
    height: 12px;
  }
}

.separate-line {
  display: none;
}
</style>
